/**
 * CodeMirror extension that marks the source lines of the element currently
 * selected on the canvas (EXT-870).
 *
 * Deliberately NOT the instant-apply flash: that animation means "these lines just
 * changed", and replaying it on every canvas click would be both noisy and a lie.
 * This is a quiet, persistent tint that answers "which code is this element?" and
 * stays for as long as the selection does. It also makes the accompanying scroll
 * legible — without it you land somewhere in the file with no idea which line was
 * the target.
 *
 * Dispatching `null` clears it (selection cleared, or the element has no reachable
 * stamp).
 */
import {
  StateEffect,
  StateField,
  type Extension,
  type Range,
} from '@codemirror/state';
import {Decoration, type DecorationSet, EditorView} from '@codemirror/view';
import type {VCCInstantApplyLineRange} from '../flux/actionCreators';

export const setSelectedElementHighlight =
  StateEffect.define<VCCInstantApplyLineRange | null>();

const lineDecoration = Decoration.line({class: 'cm-vccSelectedElementLine'});

const selectedElementHighlightField = StateField.define<DecorationSet>({
  create: () => Decoration.none,
  update(decorations, transaction) {
    let next = decorations.map(transaction.changes);
    for (const effect of transaction.effects) {
      if (!effect.is(setSelectedElementHighlight)) continue;
      if (effect.value === null) {
        next = Decoration.none;
        continue;
      }
      const doc = transaction.state.doc;
      // Clamp defensively: the range came from a build-time stamp, which can be
      // a build behind the document, and `doc.line()` throws out of range.
      const fromLine = Math.max(1, Math.min(effect.value.fromLine, doc.lines));
      const toLine = Math.max(
        fromLine,
        Math.min(effect.value.toLine, doc.lines)
      );
      const lines: Range<Decoration>[] = [];
      for (let line = fromLine; line <= toLine; line++) {
        lines.push(lineDecoration.range(doc.line(line).from));
      }
      next = Decoration.set(lines);
    }
    return next;
  },
  provide: (field) => EditorView.decorations.from(field),
});

const selectedElementHighlightTheme = EditorView.baseTheme({
  // Translucent so it reads on both the light and dark Spring editor themes, and
  // cool-toned so it never reads as the warm instant-apply flash. No animation —
  // this state persists while the element stays selected.
  '.cm-vccSelectedElementLine': {
    backgroundColor: 'rgba(90, 150, 255, 0.13)',
  },
});

export const selectedElementHighlightExtension: Extension = [
  selectedElementHighlightField,
  selectedElementHighlightTheme,
];
